/**
 * Auth Callback Handler
 * Handles OAuth, magic link, and password recovery returns from Supabase
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

export const AuthCallback: React.FC = () => {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Completing sign in...');
  const [isRecovery, setIsRecovery] = useState(false);
  const [isEmailConfirmation, setIsEmailConfirmation] = useState(false);

  useEffect(() => {
    // Handle the callback from Supabase auth
    const handleCallback = async () => {
      try {
        console.log('[AuthCallback] Starting auth callback handler...');
        console.log('[AuthCallback] Full URL:', window.location.href);
        console.log('[AuthCallback] Hash:', window.location.hash);
        console.log('[AuthCallback] Search:', window.location.search);

        // Check URL params for auth type
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);

        // Check for errors in URL (e.g., expired magic link)
        const urlError = hashParams.get('error') || searchParams.get('error');
        const errorCode = hashParams.get('error_code') || searchParams.get('error_code');
        const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');

        if (urlError) {
          console.error('[AuthCallback] Auth error in URL:', { urlError, errorCode, errorDescription });
          setStatus('error');

          // User-friendly error messages
          if (errorCode === 'otp_expired') {
            setMessage('This sign-in link has expired. Please request a new one.');
          } else {
            setMessage(decodeURIComponent(errorDescription || 'Authentication failed. Please try again.'));
          }

          setTimeout(() => setLocation('/login?error=' + errorCode), 3000);
          return;
        }

        // Check if this is a password recovery flow or email confirmation
        const type = hashParams.get('type') || searchParams.get('type');
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const code = searchParams.get('code');

        console.log('[AuthCallback] Parsed params:', { type, hasAccessToken: !!accessToken, hasRefreshToken: !!refreshToken, hasCode: !!code });

        // Check if this is an email confirmation (signup verification)
        if (type === 'signup' || type === 'email') {
          setIsEmailConfirmation(true);
          setMessage('Email confirmed! Setting up your account...');
        }

        // CRITICAL: Check for recovery BEFORE normal code exchange
        // Password reset links can come with either:
        // 1. type=recovery in hash/search params (old flow)
        // 2. A code parameter that needs to be exchanged (PKCE flow)
        // We need to detect recovery intent and redirect to reset page
        if (type === 'recovery' || window.location.href.includes('recovery') || window.location.href.includes('reset')) {
          // This is a password recovery - need to exchange code or set session
          setIsRecovery(true);
          setMessage('Validating password reset link...');
          setStatus('loading');

          try {
            let recoverySession = null;
            let recoveryError = null;

            // Try to exchange code if present (PKCE flow - most common)
            if (code) {
              console.log('[AuthCallback] Exchanging recovery code for session...');
              const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
              recoverySession = data.session;
              recoveryError = exchangeError;
              console.log('[AuthCallback] Code exchange result:', { hasSession: !!recoverySession, error: recoveryError });
            }
            // Otherwise use access_token if present (direct token flow)
            else if (accessToken && refreshToken) {
              console.log('[AuthCallback] Setting recovery session from tokens...');
              const { data, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });
              recoverySession = data.session;
              recoveryError = sessionError;
              console.log('[AuthCallback] Session set result:', { hasSession: !!recoverySession, error: recoveryError });
            }

            if (recoveryError) {
              console.error('[AuthCallback] Error setting recovery session:', recoveryError);

              // Check if this is a PKCE code_verifier error (common with cross-browser password resets)
              const errorMessage = recoveryError.message?.toLowerCase() || '';
              if (errorMessage.includes('code verifier') || errorMessage.includes('code_verifier')) {
                console.log('[AuthCallback] PKCE code_verifier missing - redirecting to password reset helper');
                // Redirect to a page that lets them request a new reset link
                setLocation('/password-reset-help');
                return;
              }

              setStatus('error');
              setMessage('Failed to validate recovery link. Please try again.');
              setTimeout(() => setLocation('/login?error=recovery_failed'), 5000);
              return;
            }

            if (recoverySession) {
              console.log('[AuthCallback] Recovery session established successfully');
              // CRITICAL: Wait for session to be persisted to localStorage
              await new Promise(resolve => setTimeout(resolve, 150));

              setStatus('success');
              setMessage('Redirecting to password reset...');

              // Redirect to reset password page
              console.log('[AuthCallback] Navigating to /reset-password');
              setTimeout(() => {
                window.location.href = '/reset-password';
              }, 500);
              return;
            } else {
              console.error('[AuthCallback] No recovery session obtained');
              setStatus('error');
              setMessage('Invalid recovery link. Please request a new one.');
              setTimeout(() => setLocation('/login'), 2000);
              return;
            }
          } catch (err: any) {
            console.error('[AuthCallback] Recovery flow error:', err);
            setStatus('error');
            setMessage('An error occurred. Please try again.');
            setTimeout(() => setLocation('/login'), 2000);
            return;
          }
        }

        // Normal auth flow (OAuth or magic link)
        // Code was already extracted above, now check if we have one to exchange
        let session = null;
        let error = null;

        if (code) {
          console.log('[AuthCallback] Exchanging code for session...');
          console.log('[AuthCallback] Code length:', code.length);

          // Debug: Check if code_verifier exists in localStorage
          const allKeys = Object.keys(localStorage);
          const pkceKeys = allKeys.filter(k => k.includes('code') || k.includes('verifier'));
          console.log('[AuthCallback] PKCE-related keys in localStorage:', pkceKeys);
          pkceKeys.forEach(key => {
            const value = localStorage.getItem(key);
            console.log(`[AuthCallback]   ${key}: ${value ? value.substring(0, 20) + '...' : 'null'}`);
          });

          // Check if code_verifier is missing BEFORE attempting exchange
          const hasCodeVerifier = pkceKeys.some(key => {
            const value = localStorage.getItem(key);
            return value && value.length > 0;
          });

          if (!hasCodeVerifier) {
            console.error('[AuthCallback] ❌ PKCE code_verifier is MISSING from localStorage!');
            console.error('[AuthCallback] This usually means the link was opened in a different browser.');
            console.error('[AuthCallback] Available localStorage keys:', allKeys);

            setStatus('error');
            setMessage('This link was opened in a different browser. Redirecting to help page...');

            // Redirect to PKCE help page (no broken verification code fallback)
            setTimeout(() => {
              setLocation('/password-reset-help');
            }, 2000);
            return;
          }

          // Show progress message immediately
          setMessage('Completing sign-in with Google...');

          // FIX: exchangeCodeForSession() can hang even when auth succeeds
          // Solution: Also listen for SIGNED_IN auth state change
          const attemptExchange = async (timeoutMs: number, attempt: number = 1): Promise<{ data: any; error: any }> => {
            console.log(`[AuthCallback] Exchange attempt ${attempt} with ${timeoutMs}ms timeout`);

            return new Promise((resolve, reject) => {
              let resolved = false;

              // Listen for SIGNED_IN auth state change (reliable success indicator)
              const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
                if (event === 'SIGNED_IN' && session && !resolved) {
                  resolved = true;
                  console.log('[AuthCallback] ✅ SIGNED_IN event received, auth successful!');
                  authListener.subscription.unsubscribe();
                  resolve({ data: { session }, error: null });
                }
              });

              // Also try the normal exchange (might work, might hang)
              supabase.auth.exchangeCodeForSession(code)
                .then((result) => {
                  if (!resolved) {
                    resolved = true;
                    authListener.subscription.unsubscribe();
                    resolve(result);
                  }
                })
                .catch((err) => {
                  if (!resolved) {
                    resolved = true;
                    authListener.subscription.unsubscribe();
                    reject(err);
                  }
                });

              // Timeout fallback
              setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  authListener.subscription.unsubscribe();
                  reject(new Error(`Session exchange timeout after ${timeoutMs}ms (attempt ${attempt})`));
                }
              }, timeoutMs);
            });
          };

          try {
            // First attempt: 5 second timeout (most OAuth should complete by now)
            try {
              const { data, error: exchangeError } = await attemptExchange(5000, 1);
              session = data.session;
              error = exchangeError;
              console.log('[AuthCallback] ✅ Exchange succeeded on first attempt (< 5s)');
            } catch (firstAttemptError) {
              // First attempt timed out - try once more with 8 second timeout
              console.warn('[AuthCallback] First attempt timed out, retrying with 8s timeout...');
              setMessage('Still working on sign-in... (this might take a moment)');

              const { data, error: exchangeError } = await attemptExchange(8000, 2);
              session = data.session;
              error = exchangeError;
              console.log('[AuthCallback] ✅ Exchange succeeded on second attempt (5-13s)');
            }

            console.log('[AuthCallback] Exchange result:', {
              hasSession: !!session,
              hasError: !!error,
              errorMessage: error?.message,
              userEmail: session?.user?.email
            });

            // If error is about code_verifier, show user-friendly error
            if (error?.message?.toLowerCase().includes('code') &&
                error?.message?.toLowerCase().includes('verifier')) {
              console.error('[AuthCallback] ❌ PKCE code_verifier error detected!');
              console.error('[AuthCallback] Error:', error);
              console.error('[AuthCallback] Available localStorage keys:', allKeys);

              setStatus('error');
              setMessage('Authentication failed due to a security verification issue. Please request a new magic link in the same browser.');

              setTimeout(() => {
                setLocation('/login?error=pkce_error&hint=request_new_link');
              }, 4000);
              return;
            }

            // CRITICAL: Wait for session to be persisted to localStorage
            // This prevents race condition with SupabaseAuthContext's initializeAuth()
            if (session && !error) {
              console.log('[AuthCallback] Session received, waiting for persistence...');
              await new Promise(resolve => setTimeout(resolve, 150));
              console.log('[AuthCallback] Session persisted to localStorage');
            }
          } catch (timeoutError: any) {
            // Both attempts failed
            console.error('[AuthCallback] ❌ All exchange attempts failed:', timeoutError.message);

            // For OAuth timeouts, redirect to login to retry
            if (!type) { // Only for OAuth (not recovery flows)
              console.log('[AuthCallback] OAuth timeout, redirecting to login to retry');
              setStatus('error');
              setMessage('Sign-in is taking too long. Redirecting to try again...');

              // Redirect to login page with retry option (no broken verification code fallback)
              setTimeout(() => {
                const email = searchParams.get('email') || '';
                setLocation(`/login?error=oauth_timeout&hint=retry${email ? '&email=' + encodeURIComponent(email) : ''}`);
              }, 2000);
              return;
            }

            error = timeoutError;
          }
        } else {
          console.log('[AuthCallback] Getting existing session...');
          const { data, error: sessionError } = await supabase.auth.getSession();
          session = data.session;
          error = sessionError;
        }
        
        if (error) {
          console.error('Auth callback error:', error);
          setStatus('error');
          setMessage('Authentication failed. Redirecting to login...');
          setTimeout(() => setLocation('/login?error=' + encodeURIComponent(error.message)), 2000);
          return;
        }

        if (session) {
          // Successfully authenticated
          setStatus('success');

          // Different messages based on auth type
          if (isEmailConfirmation) {
            setMessage('✅ Email verified! Welcome to Hover!');

            // Store flag for first-time login welcome
            localStorage.setItem('show_welcome_message', 'true');
            localStorage.setItem('email_confirmed_at', new Date().toISOString());

            // Redirect to login with success message
            setTimeout(() => {
              setLocation('/login?verified=true');
            }, 2000);
          } else {
            setMessage('Sign in successful! Redirecting...');

            // Redirect to /life - App.tsx will show onboarding automatically if needed
            // (via useFirstTimeUser hook checking profiles.onboarding_completed)
            setTimeout(() => {
              setLocation('/life');
            }, 1500);
          }
        } else {
          // No session, redirect to login
          setStatus('error');
          setMessage('No valid session found. Redirecting to login...');
          setTimeout(() => setLocation('/login'), 2000);
        }
      } catch (err) {
        console.error('Unexpected error during auth callback:', err);
        setStatus('error');
        setMessage('An unexpected error occurred. Redirecting to login...');
        setTimeout(() => setLocation('/login?error=unexpected'), 2000);
      }
    };

    handleCallback();
  }, [setLocation]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-black flex items-center justify-center">
      <div className="text-center max-w-md p-8">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white">{message}</h2>
            <p className="text-purple-300 mt-2">Please wait while we process your request...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white">
              {isRecovery ? 'Recovery Link Verified!' : 'Sign In Successful!'}
            </h2>
            <p className="text-purple-300 mt-2">{message}</p>
            {isRecovery && (
              <button
                onClick={() => {
                  console.log('[AuthCallback] Manual redirect button clicked');
                  window.location.href = '/reset-password';
                }}
                className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition"
              >
                Continue to Password Reset
              </button>
            )}
          </>
        )}
        
        {status === 'error' && (
          <>
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white">Authentication Error</h2>
            <p className="text-purple-300 mt-2">{message}</p>
          </>
        )}
      </div>
    </div>
  );
};